<script setup lang="ts">
  import {
    computed,
    defineAsyncComponent,
    inject,
    nextTick,
    ref,
    watch
  } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { storeToRefs } from 'pinia'
  import NavbarMenu from '@/components/NavbarMenu.vue'
  import { useUserStore } from '@/store/user'
  import Omnibox from './Omnibox.vue'
  import type VoiceModeModalT from '@/components/VoiceModeModal.vue'

  // Lazy : ces composants tirent tout le SDK IA (ai + @ai-sdk/*, ~1 Mo). Ils ne
  // sont rendus que sous v-if="aiEnabled" et sur interaction → hors bundle initial.
  const VoiceModeModal = defineAsyncComponent(
    () => import('@/components/VoiceModeModal.vue')
  )
  import {
    ChevronDownIcon,
    GlobeEuropeAfricaIcon,
    SparklesIcon,
    ViewColumnsIcon
  } from '@heroicons/vue/24/outline'
  import { useI18n } from 'vue-i18n'
  import topicConfig from '@/config/deck/topics.json'
  import { preferredLocale } from '@/plugins/i18n'

  const { t } = useI18n({ useScope: 'global' })
  const { aiEnabled } = storeToRefs(useUserStore())
  const topics = computed(() => topicConfig[preferredLocale.value])
  const topicsDropdown = ref<HTMLUListElement | null>(null)
  const appName = inject('appName') as string

  const voiceModalRef = ref<InstanceType<typeof VoiceModeModalT> | null>(null)

  const route = useRoute()
  const router = useRouter()

  // On observe aussi `voiceModalRef` : le modal est async, donc au boot avec
  // `?voice=true` le ref est encore null ; il se remplit une fois le chunk monté
  // et le watch se redéclenche pour ouvrir.
  watch(
    [() => route.query.voice, voiceModalRef],
    async ([voice]) => {
      await nextTick()
      if (voice === 'true') {
        voiceModalRef.value?.open()
      } else {
        voiceModalRef.value?.close()
      }
    },
    { immediate: true }
  )

  const tabs = computed(() => [
    {
      key: 'deck',
      to: { name: 'deck' as const },
      icon: ViewColumnsIcon,
      label: t('nav.deck'),
      active: route.name === 'deck' || route.name === 'document'
    },
    {
      key: 'map',
      to: { name: 'map' as const },
      icon: GlobeEuropeAfricaIcon,
      label: t('nav.map'),
      active: route.path.startsWith('/map')
    },
    ...(aiEnabled.value
      ? [
          {
            key: 'chat',
            to: { name: 'ai' as const },
            icon: SparklesIcon,
            label: t('nav.chat'),
            active: route.path.startsWith('/chat'),
            tour: 'ai-button'
          }
        ]
      : [])
  ])

  function onVoiceModalClose() {
    if (route.query.voice) {
      router.replace({ query: { ...route.query, voice: undefined } })
    }
  }
</script>

<template>
  <nav
    class="navbar bg-base-200 min-h-auto flex-nowrap gap-1 px-3 uppercase shadow-sm select-none sm:gap-2 lg:px-4 print:hidden"
    role="navigation"
    aria-label="main navigation"
  >
    <router-link
      key="home"
      :to="{ name: 'deck' }"
      name="home"
      aria-label="home"
      class="btn btn-ghost h-auto shrink-0 border-transparent bg-transparent p-0"
    >
      <img
        :alt="appName"
        src="@/assets/img/afpdeck_logo.png"
        width="42"
        height="42"
        class="size-8 sm:size-[42px]"
      />
      <span class="sr-only">Deck</span>
    </router-link>
    <Omnibox
      class="order-2 flex sm:order-1 sm:flex-1"
      key="omnibox"
      @open-voice="voiceModalRef?.open()"
    />
    <template v-if="aiEnabled">
      <VoiceModeModal
        ref="voiceModalRef"
        @close="onVoiceModalClose"
      />
    </template>
    <div
      class="border-base-300 order-1 ms-auto flex items-center gap-1 border-e pe-1 sm:order-2 sm:ms-0 sm:border-0 sm:pe-0 lg:ms-4 lg:gap-1"
    >
      <div
        v-for="tab in tabs"
        :key="tab.key"
        class="rounded-field flex items-center"
        :class="{ 'bg-primary/10': tab.active }"
      >
        <RouterLink
          :to="tab.to"
          class="btn btn-ghost btn-square tracking-wide lg:w-auto lg:px-4"
          :aria-current="tab.active ? 'page' : undefined"
          :aria-label="tab.label"
          :title="tab.label"
          :data-tour="'tour' in tab ? tab.tour : undefined"
        >
          <component
            :is="tab.icon"
            class="size-5"
          />
          <span class="hidden lg:inline">{{ tab.label }}</span>
        </RouterLink>
        <button
          v-if="tab.key === 'deck'"
          id="topics-button"
          class="btn btn-ghost w-6 px-0"
          popovertarget="topics-dropdown"
          :aria-label="t('add-column.topic-title')"
          :title="t('add-column.topic-title')"
        >
          <ChevronDownIcon class="size-4" />
        </button>
      </div>
    </div>
    <ul
      id="topics-dropdown"
      ref="topicsDropdown"
      class="dropdown nav-sheet menu rounded-box bg-base-100 max-h-[80dvh] w-60 flex-nowrap overflow-y-auto normal-case shadow-sm"
      popover
    >
      <li
        v-for="topic in topics"
        :key="topic.value"
      >
        <RouterLink
          :to="{
            name: 'deck-new',
            query: { topic: topic.value, lang: preferredLocale }
          }"
          replace
          @click="topicsDropdown?.hidePopover()"
        >
          {{ topic.label }}
        </RouterLink>
      </li>
    </ul>
    <NavbarMenu class="order-3 shrink-0" />
  </nav>
</template>

<style lang="css" scoped>
  #topics-button {
    anchor-name: --topics-list;
  }
  #topics-dropdown {
    position-anchor: --topics-list;
  }
</style>
